import { c as _c } from "react/compiler-runtime";
import { deleteEntitiesByFpIdInvestigationsByWorkflowIdArtifactsByKeyMutation, deleteOrgArtifactsByArtifactIdMutation, getEntitiesByFpIdOnboardingsByOnboardingIdDataOptions, getOrgArtifactsByArtifactId, getOrgArtifactsOptions } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import type { OrgArtifact } from '@onefootprint/request-types/dashboard';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type React from 'react';
import usePermissions from '@/hooks/use-permissions';
import { isPermissionError } from '@/utils/request-errors';
export type StoredBlock = {
  key: string;
  filename: string;
  title?: string;
  // Unset on artifact-backed blocks until `useBlockContents` fetches it.
  content?: string;
  metadata?: Record<string, unknown>;
  icon?: React.ComponentType<{
    className?: string;
  }>;
  artifactId?: string;
};
export type BlocksState = {
  blocks?: StoredBlock[];
  isLoading?: boolean;
  hasPermissionError?: boolean;
  refreshBlocks: () => void;
  deleteBlock: (block: StoredBlock) => void;
};
export const useBlocks = t0 => {
  const $ = _c(38);
  const {
    fpId,
    onboardingId,
    investigationId
  } = t0;
  const queryClient = useQueryClient();
  const showErrorToast = useRequestErrorToast();
  const {
    hasPermission
  } = usePermissions();
  let t1;
  if ($[0] !== hasPermission) {
    t1 = hasPermission("manual_review");
    $[0] = hasPermission;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const canManuallyReview = t1;
  let t2;
  if ($[2] !== fpId || $[3] !== onboardingId) {
    t2 = getEntitiesByFpIdOnboardingsByOnboardingIdDataOptions({
      path: {
        fpId,
        onboardingId
      }
    });
    $[2] = fpId;
    $[3] = onboardingId;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const dataQueryOptions = t2;
  let t3;
  if ($[5] !== investigationId) {
    t3 = getOrgArtifactsOptions({
      query: {
        investigationId,
        pageSize: ARTIFACTS_PAGE_SIZE
      }
    });
    $[5] = investigationId;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const artifactsQueryOptions = t3;
  const t4 = !!fpId && !!onboardingId && canManuallyReview;
  let t5;
  if ($[7] !== dataQueryOptions || $[8] !== t4) {
    t5 = {
      ...dataQueryOptions,
      enabled: t4,
      select: _temp
    };
    $[7] = dataQueryOptions;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const artifactsQuery = useQuery(t5);
  const t6 = !!investigationId && canManuallyReview;
  let t7;
  if ($[10] !== artifactsQueryOptions || $[11] !== t6) {
    t7 = {
      ...artifactsQueryOptions,
      enabled: t6,
      select: _temp2
    };
    $[10] = artifactsQueryOptions;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const uiBlockArtifactsQuery = useQuery(t7);
  let t8;
  if ($[13] !== artifactsQueryOptions || $[14] !== dataQueryOptions || $[15] !== queryClient) {
    t8 = () => {
      queryClient.invalidateQueries({
        queryKey: dataQueryOptions.queryKey
      });
      queryClient.invalidateQueries({
        queryKey: artifactsQueryOptions.queryKey
      });
    };
    $[13] = artifactsQueryOptions;
    $[14] = dataQueryOptions;
    $[15] = queryClient;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  const refreshBlocks = t8;
  let t9;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = deleteEntitiesByFpIdInvestigationsByWorkflowIdArtifactsByKeyMutation();
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  const deleteBlockMutation = useMutation(t9);
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = deleteOrgArtifactsByArtifactIdMutation();
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  const deleteArtifactBlockMutation = useMutation(t10);
  let t11;
  if ($[19] !== deleteArtifactBlockMutation || $[20] !== deleteBlockMutation || $[21] !== fpId || $[22] !== onboardingId || $[23] !== refreshBlocks || $[24] !== showErrorToast) {
    t11 = block => {
      if (block.artifactId) {
        deleteArtifactBlockMutation.mutate({
          path: {
            artifactId: block.artifactId
          }
        }, {
          onSuccess: refreshBlocks,
          onError: showErrorToast
        });
        return;
      }
      if (!fpId || !onboardingId) {
        return;
      }
      deleteBlockMutation.mutate({
        path: {
          fpId,
          workflowId: onboardingId,
          key: block.key
        }
      }, {
        onSuccess: refreshBlocks,
        onError: showErrorToast
      });
    };
    $[19] = deleteArtifactBlockMutation;
    $[20] = deleteBlockMutation;
    $[21] = fpId;
    $[22] = onboardingId;
    $[23] = refreshBlocks;
    $[24] = showErrorToast;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  const deleteBlock = t11;
  let t12;
  if ($[26] !== artifactsQuery.data || $[27] !== uiBlockArtifactsQuery.data) {
    const artifactBlocks = (uiBlockArtifactsQuery.data ?? []).map(artifactToStoredBlock).filter(_temp3);
    const legacyBlocks = (artifactsQuery.data ?? []).filter(_temp4).map(_temp5);
    const artifactKeys = new Set(artifactBlocks.map(_temp6));
    t12 = [...artifactBlocks, ...legacyBlocks.filter(block_2 => !artifactKeys.has(block_2.key))].sort(_temp7);
    $[26] = artifactsQuery.data;
    $[27] = uiBlockArtifactsQuery.data;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const blocks = t12;
  const t13 = artifactsQuery.isLoading || uiBlockArtifactsQuery.isLoading;
  let t14;
  if ($[29] !== artifactsQuery.error || $[30] !== canManuallyReview) {
    t14 = !canManuallyReview || isPermissionError(artifactsQuery.error);
    $[29] = artifactsQuery.error;
    $[30] = canManuallyReview;
    $[31] = t14;
  } else {
    t14 = $[31];
  }
  let t15;
  if ($[32] !== blocks || $[33] !== deleteBlock || $[34] !== refreshBlocks || $[35] !== t13 || $[36] !== t14) {
    t15 = {
      blocks,
      isLoading: t13,
      hasPermissionError: t14,
      deleteBlock,
      refreshBlocks
    };
    $[32] = blocks;
    $[33] = deleteBlock;
    $[34] = refreshBlocks;
    $[35] = t13;
    $[36] = t14;
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  return t15;
};

// Split from `useBlocks` so the page reads block bytes only where the blocks render (the Generated
// Summary), not on every load: each read is a logged artifact fetch.
export const useBlockContents = blocks => {
  const $ = _c(7);
  const artifactIds = blocks.flatMap(_temp8);
  const contents = useQueries({
    queries: artifactIds.map(_temp9)
  });
  let t0;
  if ($[0] !== contents) {
    t0 = (artifactId_0, index) => [artifactId_0, contents[index]?.data];
    $[0] = contents;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const contentById = new Map(artifactIds.map(t0));
  const t1 = blocks.flatMap(block_0 => {
    const content = block_0.artifactId ? contentById.get(block_0.artifactId) : block_0.content;
    return content ? [{
      ...block_0,
      content
    }] : [];
  });
  let t2;
  if ($[2] !== contents) {
    t2 = contents.some(_temp10);
    $[2] = contents;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = {
      blocks: t1,
      isLoading: t2
    };
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
};

// Matches backend MAX_PAGE_SIZE for the org artifacts endpoint.
const ARTIFACTS_PAGE_SIZE = 100;
const isUiBlockKey = (key: string) => key.startsWith('ui_block_') && key.endsWith('.jsonl');
const isUiBlockArtifact = (artifact: OrgArtifact) => artifact.metadata.kind === 'ui_block';
const artifactToStoredBlock = (artifact: OrgArtifact): StoredBlock | undefined => {
  if (artifact.metadata.kind !== 'ui_block') return undefined;
  const {
    data
  } = artifact.metadata;
  return {
    key: data.key,
    filename: artifact.name,
    title: data.title ?? artifact.name,
    metadata: data.schemaVersion ? {
      schema_version: data.schemaVersion
    } : undefined,
    artifactId: artifact.id
  };
};
function _temp(res) {
  return res.data;
}
function _temp2(res_0) {
  return res_0.data.filter(isUiBlockArtifact);
}
function _temp3(block_0) {
  return !!block_0;
}
function _temp4(a) {
  return isUiBlockKey(a.key);
}
function _temp5(a_0) {
  return {
    key: a_0.key,
    filename: a_0.key,
    title: a_0.schema?.kind === "file" ? a_0.schema.data.name : undefined,
    content: typeof a_0.value === "string" ? a_0.value : JSON.stringify(a_0.value),
    metadata: a_0.schema?.kind === "file" ? a_0.schema.data.metadata : undefined
  };
}
function _temp6(block_1) {
  return block_1.key;
}
function _temp7(a_1, b) {
  return a_1.key.localeCompare(b.key);
}
function _temp8(block) {
  return block.artifactId ?? [];
}
function _temp9(artifactId) {
  return {
    queryKey: ["gen-ui-block-artifact-content", artifactId],
    queryFn: async () => {
      const response = await getOrgArtifactsByArtifactId({
        path: {
          artifactId
        },
        parseAs: "blob",
        throwOnError: true
      });
      return (response.data as Blob).text();
    }
  };
}
function _temp10(query) {
  return query.isLoading;
}